import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { Parent, ParentNote, Contact, ClientDocument, ClientFile } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

type File = ClientFile;
type Document = ClientDocument;

const parentCollectionRef = collection(db, "parents");

export const ParentService = {
  async createParent(
    parentData: Omit<Parent, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newParent: Omit<Parent, "id"> = {
      ...parentData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const parentRef = await addDoc(parentCollectionRef, clean(newParent));
    return parentRef.id;
  },

  async getParent(parentId: string): Promise<Parent | null> {
    const parentDoc = await getDoc(doc(parentCollectionRef, parentId));
    if (parentDoc.exists()) {
      return { id: parentDoc.id, ...parentDoc.data() } as Parent;
    }
    return null;
  },

  async getAllParents(accountId: string): Promise<Parent[]> {
    const parentSnapshot = await getDocs(
      query(parentCollectionRef, where("accountId", "==", accountId)),
    );
    return parentSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Parent,
    );
  },

  async updateParent(
    parentId: string,
    updatedData: Partial<Omit<Parent, "id" | "createdAt">>,
  ): Promise<void> {
    const parentDocRef = doc(parentCollectionRef, parentId);
    await updateDoc(parentDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteParent(parentId: string): Promise<void> {
    const parentDocRef = doc(parentCollectionRef, parentId);
    await deleteDoc(parentDocRef);
  },

  async getParentFiles(parentId: string): Promise<File[]> {
    const parentFilesSnapshot = await getDocs(
      collection(db, "parents", parentId, "files"),
    );
    if (parentFilesSnapshot.empty) {
      return [];
    }
    return parentFilesSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as File,
    );
  },

  async getParentContacts(
    parentId: string,
    accountId: string,
  ): Promise<Contact[]> {
    const parentContactsSnapshot = await getDocs(
      query(
        collection(db, "parents", parentId, "contacts"),
        where("accountId", "==", accountId),
      ),
    );
    if (parentContactsSnapshot.empty) {
      return [];
    }
    return parentContactsSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Contact,
    );
  },

  async getParentDocuments(parentId: string, accountId: string): Promise<Document[]> {
    const parentDocumentsSnapshot = await getDocs(
      query(
        collection(db, "documents"),
        where("parentId", "==", parentId),
        where("accountId", "==", accountId),
      ),
    );
    if (parentDocumentsSnapshot.empty) {
      return [];
    }
    return parentDocumentsSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Document,
    );
  },

  async addFileToParent(
    parentId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newFile: Omit<File, "id"> = {
      ...fileData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const fileRef = await addDoc(
      collection(db, "parents", parentId, "files"),
      newFile,
    );
    return fileRef.id;
  },

  async updateFileInParent(
    parentId: string,
    fileId: string,
    updatedData: Partial<Omit<File, "id" | "createdAt" | "updatedAt">>,
  ): Promise<void> {
    const fileRef = doc(collection(db, "parents", parentId, "files"), fileId);
    await updateDoc(fileRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteFileFromParent(parentId: string, fileId: string): Promise<void> {
    const fileRef = doc(collection(db, "parents", parentId, "files"), fileId);
    await deleteDoc(fileRef);
  },

  async deleteContactFromParent(
    parentId: string,
    contactId: string,
  ): Promise<void> {
    const contactRef = doc(
      collection(db, "parents", parentId, "contacts"),
      contactId,
    );
    await deleteDoc(contactRef);
  },

  // ============================================================================
  // PARENT NOTES
  // ============================================================================

  async getParentNotes(parentId: string): Promise<ParentNote[]> {
    const notesSnapshot = await getDocs(
      query(
        collection(db, "parents", parentId, "notes"),
        orderBy("createdAt", "desc"),
      ),
    );
    return notesSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as ParentNote,
    );
  },

  async addNoteToParent(
    parentId: string,
    noteData: Omit<ParentNote, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newNote: Omit<ParentNote, "id"> = {
      ...noteData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const noteRef = await addDoc(
      collection(db, "parents", parentId, "notes"),
      newNote,
    );
    return noteRef.id;
  },

  async updateParentNote(
    parentId: string,
    noteId: string,
    content: string,
  ): Promise<void> {
    const noteRef = doc(
      collection(db, "parents", parentId, "notes"),
      noteId,
    );
    await updateDoc(noteRef, {
      content,
      updatedAt: Timestamp.now(),
    });
  },

  async deleteNoteFromParent(
    parentId: string,
    noteId: string,
  ): Promise<void> {
    const noteRef = doc(
      collection(db, "parents", parentId, "notes"),
      noteId,
    );
    await deleteDoc(noteRef);
  },
};
